import apiService from './api';

export interface LicenseData {
  id: string;
  code: string;
  type: string;
  username: string;
  expiryDate: string;
  isValid: boolean;
  isActive: boolean;
  hwid?: string;
  vpsIp?: string;
  vpsPort?: string;
  grabber?: boolean;
  mailer?: boolean;
  debouncer?: boolean;
  stripe?: boolean;
  premiumProxy?: boolean;
}

export interface LicenseInfo {
  code: string;
  type: string;
  expiryDate: string;
  isValid: boolean;
  username?: string;
  hwid?: string;
  vpsIp?: string;
  vpsPort?: string;
  grabber?: boolean;
  mailer?: boolean;
  debouncer?: boolean;
  stripe?: boolean;
  premiumProxy?: boolean;
  message?: string;
}

export interface LicenseValidatorConfig {
  validationInterval: number; // en millisecondes (défaut: 60000 = 1 minute)
  retryAttempts: number; // nombre de tentatives avant déconnexion (défaut: 3)
  onLicenseInvalid: (licenseInfo: LicenseInfo) => void;
  onValidationError: (error: Error) => void;
}

export class LicenseValidator {
  private apiService: typeof apiService;
  private validationTimer: NodeJS.Timeout | null = null;
  private retryCount: number = 0;
  private config: LicenseValidatorConfig;
  private isRunning: boolean = false;

  constructor(config: LicenseValidatorConfig) {
    this.config = {
      validationInterval: 60000, // 1 minute par défaut
      retryAttempts: 3,
      onLicenseInvalid: () => {},
      onValidationError: () => {}
    };
    
    // Appliquer la configuration passée par-dessus les valeurs par défaut
    Object.assign(this.config, config);
    this.apiService = apiService;
  }

  /**
   * Démarre la validation périodique des licences
   */
  public startValidation(): void {
    if (this.isRunning) {
      console.warn('LicenseValidator: Validation déjà en cours');
      return;
    }

    this.isRunning = true;
    console.log(`LicenseValidator: Démarrage de la validation périodique (intervalle: ${this.config.validationInterval}ms)`);
    
    this.validateCurrentLicense();
    this.validationTimer = setInterval(() => {
      this.validateCurrentLicense();
    }, this.config.validationInterval);
  }

  /**
   * Arrête la validation périodique
   */
  public stopValidation(): void {
    if (this.validationTimer) {
      clearInterval(this.validationTimer);
      this.validationTimer = null;
    }
    this.isRunning = false;
    this.retryCount = 0;
    console.log('LicenseValidator: Validation périodique arrêtée');
  }

  /**
   * Valide la licence actuelle
   */
  private async validateCurrentLicense(): Promise<void> {
    try {
      // Récupérer la licence depuis localStorage
      const savedLicense = localStorage.getItem('user_license');
      const savedUser = localStorage.getItem('license_user');

      if (!savedLicense || !savedUser) {
        console.log('LicenseValidator: Aucune licence trouvée dans localStorage');
        this.handleLicenseInvalid(null);
        return;
      }

      const licenseInfo: LicenseInfo = JSON.parse(savedLicense);
      const userData = JSON.parse(savedUser);

      // Appeler le service API pour vérifier la licence
      const isValid = await this.checkLicenseValidity(licenseInfo.code);

      if (!isValid) {
        console.log('LicenseValidator: Licence invalide détectée');
        this.retryCount++;
        
        if (this.retryCount >= this.config.retryAttempts) {
          console.log(`LicenseValidator: Licence invalide après ${this.config.retryAttempts} tentatives - Déconnexion`);
          this.handleLicenseInvalid(licenseInfo);
          return;
        }
        
        console.log(`LicenseValidator: Tentative ${this.retryCount}/${this.config.retryAttempts} - Réessayer plus tard`);
        return;
      }

      // Licence valide - réinitialiser le compteur de retry
      this.retryCount = 0;

      // Vérifier l'expiration
      if (licenseInfo.expiryDate && new Date() > new Date(licenseInfo.expiryDate)) {
        console.log('LicenseValidator: Licence expirée');
        this.handleLicenseInvalid(licenseInfo);
        return;
      }

      console.log('LicenseValidator: Licence valide');

    } catch (error) {
      console.error('LicenseValidator: Erreur lors de la validation:', error);
      this.config.onValidationError(error as Error);
      
      this.retryCount++;
      if (this.retryCount >= this.config.retryAttempts) {
        console.log(`LicenseValidator: Erreur après ${this.config.retryAttempts} tentatives`);
        this.handleLicenseInvalid(null);
      }
    }
  }

  /**
   * Vérifie la validité de la licence via l'API
   */
  private async checkLicenseValidity(licenseCode: string): Promise<boolean> {
    try {
      const licenseInfo = await this.apiService.verifyLicense(licenseCode);
      return licenseInfo.isValid;
    } catch (error) {
      console.error('LicenseValidator: Erreur API validation licence:', error);
      throw error;
    }
  }

  /**
   * Traite le cas où la licence devient invalide
   */
  private handleLicenseInvalid(licenseInfo: LicenseInfo | null): void {
    console.log('LicenseValidator: Déconnexion de l\'utilisateur - licence invalide');
    this.stopValidation();
    
    // Nettoyer le localStorage
    localStorage.removeItem('user_license');
    localStorage.removeItem('license_user');
    
    // Déclencher l'événement de déconnexion
    window.dispatchEvent(new CustomEvent('licenseInvalidated', { 
      detail: { licenseInfo, timestamp: new Date() } 
    }));
    
    // Appeler le callback de notification
    if (licenseInfo) {
      this.config.onLicenseInvalid(licenseInfo);
    }
  }

  /**
   * Force une validation immédiate
   */
  public async forceValidation(): Promise<boolean> {
    try {
      const savedLicense = localStorage.getItem('user_license');
      if (!savedLicense) {
        return false;
      }

      const licenseInfo: LicenseInfo = JSON.parse(savedLicense);
      const isValid = await this.checkLicenseValidity(licenseInfo.code);
      
      if (!isValid) {
        this.handleLicenseInvalid(licenseInfo);
      }
      
      return isValid;
    } catch (error) {
      this.config.onValidationError(error as Error);
      return false;
    }
  }

  /**
   * Valide que toutes les licences multiples sont bien différentes
   */
  public validateLicenseUniqueness(): boolean {
    try {
      const savedLicenses = localStorage.getItem('multiple_licenses');
      if (!savedLicenses) {
        return true; // Pas de licences multiples, pas de doublons
      }

      const licenses: any[] = JSON.parse(savedLicenses);
      
      // Vérifier les doublons par code de licence
      const codes = licenses.map(l => l.code);
      const uniqueCodes = new Set(codes);
      if (codes.length !== uniqueCodes.size) {
        console.warn('⚠️ Licences en doublon détectées par code');
        return false;
      }

      // Vérifier les doublons par HWID
      const hwids = licenses.map(l => l.hwid).filter(hwid => hwid);
      const uniqueHwids = new Set(hwids);
      if (hwids.length !== uniqueHwids.size) {
        console.warn('⚠️ Licences en doublon détectées par HWID');
        return false;
      }

      // Vérifier les doublons par username
      const usernames = licenses.map(l => l.username);
      const uniqueUsernames = new Set(usernames);
      if (usernames.length !== uniqueUsernames.size) {
        console.warn('⚠️ Licences en doublon détectées par username');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erreur validation unicité licences:', error);
      return false;
    }
  }

  /**
   * Nettoie les licences dupliquées
   */
  public cleanDuplicateLicenses(): LicenseData[] {
    try {
      const savedLicenses = localStorage.getItem('multiple_licenses');
      if (!savedLicenses) {
        return [];
      }

      const licenses: LicenseData[] = JSON.parse(savedLicenses);
      const uniqueLicenses: LicenseData[] = [];
      
      // Garder seulement la première occurrence de chaque licence unique
      const seenCodes = new Set<string>();
      const seenHwids = new Set<string>();
      const seenUsernames = new Set<string>();

      for (const license of licenses) {
        if (
          !seenCodes.has(license.code) &&
          !seenHwids.has(license.hwid || '') &&
          !seenUsernames.has(license.username)
        ) {
          uniqueLicenses.push(license);
          seenCodes.add(license.code);
          if (license.hwid) seenHwids.add(license.hwid);
          seenUsernames.add(license.username);
        }
      }

      // Sauvegarder les licences nettoyées
      if (uniqueLicenses.length !== licenses.length) {
        localStorage.setItem('multiple_licenses', JSON.stringify(uniqueLicenses));
      }

      return uniqueLicenses;
    } catch (error) {
      console.error('Erreur nettoyage licences dupliquées:', error);
      return [];
    }
  }

  /**
   * Vérifie si la validation est en cours
   */
  public get isValidationRunning(): boolean {
    return this.isRunning;
  };

  /**
   * Obtient la configuration actuelle
   */
  public get configuration(): LicenseValidatorConfig {
    return { ...this.config };
  }

  /**
   * Met à jour la configuration
   */
  public updateConfig(newConfig: Partial<LicenseValidatorConfig>): void {
    this.config = { ...this.config, ...newConfig };
    console.log('LicenseValidator: Configuration mise à jour', this.config);
  }
}

// Instance singleton du validateur de licence
let licenseValidatorInstance: LicenseValidator | null = null;

/**
 * Obtient l'instance singleton du validateur de licence
 */
export function getLicenseValidator(config?: LicenseValidatorConfig): LicenseValidator {
  if (!licenseValidatorInstance && config) {
    licenseValidatorInstance = new LicenseValidator(config);
  }
  
  if (!licenseValidatorInstance) {
    throw new Error('LicenseValidator: Pas de configuration fournie pour la première initialisation');
  }
  
  return licenseValidatorInstance;
}

/**
 * Crée une nouvelle instance du validateur de licence
 */
export function createLicenseValidator(config: LicenseValidatorConfig): LicenseValidator {
  return new LicenseValidator(config);
}

export default LicenseValidator;
